import {
  Author,
  toAuthor,
  toAuthorPostReqDTO,
  toAuthorPutReqDTO,
} from "../model/author";
import { AuthorGetResDTO } from "./author-dto";
import { getApiClient } from "../../../shared/api/client.api";
import { CommonResponseDto } from "../../../shared/api/common-response.dto";

/**
 * Author API 클라이언트
 * Author 관련 API 호출을 담당
 */
export const manageAuthorApi = (() => {
  const apiClient = getApiClient();

  const getAuthorList = async (): Promise<Author[]> => {
    try {
      const response =
        await apiClient.get<CommonResponseDto<AuthorGetResDTO[]>>("/author");

      if (!response.data.success || !response.data.data) {
        throw new Error("Author list data not found");
      }

      return response.data.data.map((author: AuthorGetResDTO) =>
        toAuthor(author),
      );
    } catch (error) {
      console.error("Failed to fetch author list:", error);
      throw error;
    }
  };

  const getAuthor = async (authorId: string): Promise<Author> => {
    try {
      const response = await apiClient.get<CommonResponseDto<AuthorGetResDTO>>(
        `/author/${authorId}`,
      );

      if (!response.data.success || !response.data.data) {
        throw new Error("Author data not found");
      }

      return toAuthor(response.data.data);
    } catch (error) {
      console.error("Failed to fetch author:", error);
      throw error;
    }
  };

  const addAuthor = async (
    author: Omit<Author, "id" | "createdAt" | "updatedAt">,
  ): Promise<CommonResponseDto<AuthorGetResDTO>> => {
    try {
      const payload = toAuthorPostReqDTO(author);
      const response = await apiClient.post<CommonResponseDto<AuthorGetResDTO>>(
        "/author",
        payload,
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.details || "Failed to add author");
      }

      return response.data;
    } catch (error) {
      console.error("Failed to add author:", error);
      throw error;
    }
  };

  const updateAuthor = async (
    authorId: string,
    author: Partial<Author>,
  ): Promise<CommonResponseDto<AuthorGetResDTO>> => {
    try {
      const payload = toAuthorPutReqDTO({ ...author, id: authorId });
      const response = await apiClient.put<CommonResponseDto<AuthorGetResDTO>>(
        `/author/${authorId}`,
        payload,
      );

      if (!response.data.success) {
        throw new Error(
          response.data.error?.details || "Failed to update author",
        );
      }

      return response.data;
    } catch (error) {
      console.error("Failed to update author:", error);
      throw error;
    }
  };

  const deleteAuthor = async (
    authorId: string,
  ): Promise<CommonResponseDto<void>> => {
    try {
      const response = await apiClient.delete<CommonResponseDto<void>>(
        `/author/${authorId}`,
      );

      if (!response.data.success) {
        throw new Error(
          response.data.error?.details || "Failed to delete author",
        );
      }

      return response.data;
    } catch (error) {
      console.error("Failed to delete author:", error);
      throw error;
    }
  };

  return {
    getAuthorList,
    getAuthor,
    addAuthor,
    updateAuthor,
    deleteAuthor,
  };
})();
